/**
 * Agent API - Client-Side Service
 *
 * All agent operations go through server-side Express routes
 * under /api/protected/agent.
 * Only practitioners can use the AI Agent.
 */
import type {
  ChatMessage,
  Conversation,
  ToolCall,
  ToolResult,
  MessageMetadata,
  ChatAttachment,
} from '@/components/agent/types/agent-types';
import { apiFetch } from '@/lib/api-client';
import { mcpClient } from '@/services/mcp-client';
import logger from '@/lib/logging';

// Response types
interface CreateConversationResponse {
  success: boolean;
  conversationId: string;
  message: string;
}

interface UpdateResponse {
  success: boolean;
  message: string;
}

interface ConversationListItem {
  id: string;
  userId: string;
  title: string;
  clientId?: string;
  clientName?: string;
  messageCount: number;
  createdAt: any;
  updatedAt: any;
}

interface AgentChatResponse {
  conversationId: string;
  response: string;
  toolCalls?: ToolCall[];
  toolResults?: ToolResult[];
  metadata?: MessageMetadata;
  suggestions?: string[];
  attachments?: ChatAttachment[];
}

interface ToolExecutionResponse {
  success: boolean;
  result?: any;
  error?: string;
}

export interface AgentAttachmentInput {
  id: string;
  name: string;
  type: 'text' | 'image' | 'file';
  mimeType: string;
  size: number;
  content?: string;
  dataUrl?: string;
  preview?: string;
}

/** Paths are relative to `/api/protected` (apiFetch attaches CSRF on mutating methods). */
const BASE_PATH = '/agent';

// ============ CONVERSATION OPERATIONS ============

/**
 * Create a new conversation
 */
export async function createConversation(data?: {
  title?: string;
  clientId?: string;
  clientName?: string;
}): Promise<string> {
  const response = await apiFetch<CreateConversationResponse>(`${BASE_PATH}/conversations`, {
    method: 'POST',
    body: JSON.stringify(data || {}),
  });
  return response.conversationId;
}

/**
 * Get a single conversation by ID (includes full message history)
 */
export async function getConversation(conversationId: string): Promise<Conversation | null> {
  try {
    return await apiFetch<Conversation>(`${BASE_PATH}/conversations/${conversationId}`);
  } catch (error: any) {
    if (error.message?.toLowerCase().includes('not found')) {
      return null;
    }
    throw error;
  }
}

/**
 * Get all conversations for the current user
 */
export async function getConversations(options?: {
  clientId?: string;
  limit?: number;
}): Promise<ConversationListItem[]> {
  const params = new URLSearchParams();
  if (options?.clientId) params.set('clientId', options.clientId);
  if (options?.limit) params.set('limit', String(options.limit));

  const query = params.toString();
  const url = query ? `${BASE_PATH}/conversations?${query}` : `${BASE_PATH}/conversations`;
  return apiFetch<ConversationListItem[]>(url);
}

/**
 * Update a conversation (title, client, etc.)
 */
export async function updateConversation(
  conversationId: string,
  updates: Partial<Pick<Conversation, 'title' | 'clientId' | 'clientName'>>
): Promise<void> {
  await apiFetch<UpdateResponse>(`${BASE_PATH}/conversations/${conversationId}`, {
    method: 'PATCH',
    body: JSON.stringify(updates),
  });
}

/**
 * Delete a conversation
 */
export async function deleteConversation(conversationId: string): Promise<void> {
  await apiFetch<UpdateResponse>(`${BASE_PATH}/conversations/${conversationId}`, {
    method: 'DELETE',
  });
}

// ============ CHAT OPERATIONS ============

/**
 * Send a message to Harry AI Agent
 */
export async function sendMessage(params: {
  conversationId?: string;
  message: string;
  clientId?: string;
  attachments?: AgentAttachmentInput[];
  context?: {
    includeAssessments?: boolean;
    includePostureAnalyses?: boolean;
    includePrograms?: boolean;
  };
}): Promise<AgentChatResponse> {
  try {
    return await mcpClient.sendChat<AgentChatResponse>(params);
  } catch (error) {
    logger.warn('[AgentAPI] Falling back to REST chat endpoint', error);
    return apiFetch<AgentChatResponse>(`${BASE_PATH}/chat`, {
      method: 'POST',
      body: JSON.stringify(params),
    });
  }
}

/**
 * Execute an agent tool directly
 */
export async function executeTool(
  toolName: string,
  args: Record<string, any>
): Promise<ToolExecutionResponse> {
  try {
    const result = await mcpClient.executeOperation(toolName, args);
    return {
      success: true,
      result,
    };
  } catch (error: any) {
    return {
      success: false,
      error: error?.message || 'Failed to execute MCP operation',
    };
  }
}

// ============ HELPER FUNCTIONS ============

/**
 * Get clients list via agent tool
 */
export async function getClientsViaTool(): Promise<any[]> {
  const response = await executeTool('getClients', {});
  if (response.success) {
    return response.result || [];
  }
  throw new Error(response.error || 'Failed to get clients');
}

/**
 * Get client data via agent tool
 */
export async function getClientViaTool(clientId: string): Promise<any> {
  const response = await executeTool('getClient', { clientId });
  if (response.success) {
    return response.result;
  }
  throw new Error(response.error || 'Failed to get client');
}

/**
 * Search drills via agent tool
 */
export async function searchDrillsViaTool(query: string): Promise<any[]> {
  const response = await executeTool('searchDrills', { query });
  if (response.success) {
    return response.result || [];
  }
  throw new Error(response.error || 'Failed to search drills');
}

/**
 * Get programs via agent tool
 */
export async function getProgramsViaTool(clientId?: string): Promise<any[]> {
  const response = await executeTool('getPrograms', { clientId });
  if (response.success) {
    return response.result || [];
  }
  throw new Error(response.error || 'Failed to get programs');
}

